/**
 * MarketingAssetGenerator — produces launch-ready author marketing assets.
 *
 * Generates: Amazon back-cover blurb, ARC pitch email, tweet thread,
 * Instagram caption, Facebook post — powered by LLM using the project's
 * synopsis, plot blueprint, and character data.
 *
 * Available to Pro / Studio tier users.
 * Triggered by: loom:open-marketing-assets event or File menu entry.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Sparkles, Copy, Check, Megaphone,
  Mail, Twitter, Instagram, Facebook, BookMarked, Calendar, Clock, Users,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { marketingAssetsApi } from '@/lib/api';
import ArcOutreachTab from './ArcOutreachTab';
import { cn } from '@/lib/utils';
import { usePW } from '@/lib/store';

// ─── Copy button ─────────────────────────────────────────────────────
function CopyBtn({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — please select text manually.');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      data-testid={testId}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ─── Asset card ───────────────────────────────────────────────────────
function AssetCard({ title, icon: Icon, iconColor, content, testId }) {
  const text = Array.isArray(content) ? content.join('\n\n') : (content || '');
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <span className="flex items-center gap-1.5 text-[12px] font-semibold">
          {Icon && <Icon className={cn('h-3.5 w-3.5', iconColor)} />}
          {title}
        </span>
        {text && <CopyBtn text={text} testId={`${testId}-copy`} />}
      </div>
      <div className="p-3">
        {Array.isArray(content) ? (
          <div className="space-y-2">
            {content.map((tweet, i) => (
              <div key={i} className="rounded bg-muted/30 px-2.5 py-2 text-[12.5px] leading-relaxed">
                <span className="text-[10px] font-semibold text-muted-foreground mr-1.5">
                  {i + 1}/{content.length}
                </span>
                {tweet}
              </div>
            ))}
          </div>
        ) : (
          <Textarea
            value={text}
            readOnly
            rows={text.split('\n').length + 2}
            className="resize-none text-[12.5px] leading-relaxed bg-transparent border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        )}
      </div>
    </div>
  );
}

// ─── Social Scheduler ─────────────────────────────────────────────────
function SocialSchedulerSection({ pid, assets }) {
  const [schedule, setSchedule]     = useState(null);
  const [loading, setLoading]       = useState(false);
  const [launchDate, setLaunchDate] = useState('');
  const hasAssets = assets && (assets.tweet_thread?.length > 0 || assets.instagram_caption || assets.facebook_post);

  useEffect(() => {
    if (!pid) return;
    marketingAssetsApi.getSchedule(pid)
      .then((d) => { if (d.social_schedule) setSchedule(d.social_schedule); })
      .catch(() => { /* no schedule yet */ });
  }, [pid]);

  const handleSchedule = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const body = launchDate ? { launch_date: launchDate } : {};
      const data = await marketingAssetsApi.createSchedule(pid, body);
      setSchedule(data.social_schedule);
      toast.success('Launch schedule created!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scheduling failed — generate assets first.');
    } finally {
      setLoading(false);
    }
  };

  const PLATFORM_ICONS = { twitter: Twitter, instagram: Instagram, facebook: Facebook };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <Calendar className="h-4 w-4 text-primary" />
        <span className="font-semibold text-[13px]">Pre-Launch Social Schedule</span>
      </div>
      <p className="text-[11.5px] text-muted-foreground">
        One post per day, starting 5 days before your launch. Set a launch date to get exact dates.
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="date"
          className="h-8 text-[12px] w-44"
          value={launchDate}
          onChange={(e) => setLaunchDate(e.target.value)}
          placeholder="Launch date (optional)"
          data-testid="social-schedule-launch-date"
        />
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 h-8 text-[11.5px]"
          onClick={handleSchedule}
          disabled={loading || !hasAssets}
          data-testid="social-schedule-create-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
          {schedule ? 'Reschedule' : 'Create schedule'}
        </Button>
      </div>
      {!hasAssets && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          Generate marketing assets first to create a schedule.
        </p>
      )}
      {schedule && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Clock className="h-3 w-3" /> Launch: <strong>{schedule.launch_date}</strong>
          </div>
          {schedule.posts?.map((post) => {
            const Icon = PLATFORM_ICONS[post.platform] || Calendar;
            return (
              <div key={post.id} className="rounded-lg border border-border p-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                    <Icon className="h-3.5 w-3.5 text-primary" />
                    {post.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10.5px] text-muted-foreground">{post.scheduled_date}</span>
                    <CopyBtn text={post.text} testId={`schedule-post-${post.post_num}-copy`} />
                  </div>
                </div>
                <p className="text-[11.5px] text-muted-foreground leading-relaxed line-clamp-3">{post.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────
export default function MarketingAssetGenerator({ open, onClose }) {
  const { project } = usePW();
  const pid = project?.id;

  const [assets, setAssets]     = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading]   = useState(false);

  // Load cached assets on open
  useEffect(() => {
    if (!open || !pid) return;
    setLoading(true);
    marketingAssetsApi.get(pid)
      .then((d) => { if (d.marketing_assets) setAssets(d.marketing_assets); })
      .catch(() => { /* no cache yet — fine */ })
      .finally(() => setLoading(false));
  }, [open, pid]);

  const handleGenerate = async () => {
    if (!pid) return;
    setGenerating(true);
    try {
      const data = await marketingAssetsApi.generate(pid);
      setAssets(data.marketing_assets);
      toast.success('Marketing assets generated!');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Generation failed — please try again.';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const generatedAt = assets?.generated_at
    ? new Date(assets.generated_at).toLocaleDateString()
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="marketing-assets-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" />
            Marketing Asset Generator
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Launch-ready copy and reviewer outreach — all in one place.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="assets" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0 w-full justify-start h-8 gap-0.5">
            <TabsTrigger value="assets" className="gap-1.5 h-7 text-[12px]" data-testid="marketing-tab-assets">
              <Megaphone className="h-3.5 w-3.5" /> Assets
            </TabsTrigger>
            <TabsTrigger value="arc" className="gap-1.5 h-7 text-[12px]" data-testid="marketing-tab-arc">
              <Users className="h-3.5 w-3.5" /> ARC Outreach
            </TabsTrigger>
          </TabsList>

          {/* ── Assets tab ── */}
          <TabsContent value="assets" className="flex-1 overflow-hidden flex flex-col mt-0 pt-3">
            {/* Action bar */}
            <div className="flex items-center gap-2 flex-shrink-0 mb-3">
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={generating || loading}
                className="gap-1.5"
                data-testid="marketing-assets-generate-btn"
              >
                {generating
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                  : <><Sparkles className="h-3.5 w-3.5" />{assets ? 'Regenerate' : 'Generate assets'}</>}
              </Button>
              {generatedAt && (
                <span className="ml-auto text-[10.5px] text-muted-foreground">
                  Last generated {generatedAt}
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1 scroll-thin">
              {loading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground text-[13px]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : generating ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <p className="text-[13px]">Crafting your marketing copy…</p>
                  <p className="text-[11.5px]">This usually takes 20–30 seconds.</p>
                </div>
              ) : !assets ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
                  <Megaphone className="mx-auto mb-2 h-7 w-7 text-muted-foreground/40" />
                  <p className="text-[13px] font-medium text-muted-foreground">No assets generated yet.</p>
                  <p className="text-[12px] text-muted-foreground/70 mt-1">
                    Click <strong>Generate assets</strong> to create your launch copy from the manuscript.
                  </p>
                  <p className="text-[11px] text-muted-foreground/50 mt-2">
                    For best results, run <strong>Synopsis</strong> and <strong>Plot Builder</strong> first.
                  </p>
                </div>
              ) : (
                <>
                  <AssetCard
                    title="Amazon Back-Cover Blurb"
                    icon={BookMarked}
                    iconColor="text-orange-500"
                    content={assets.amazon_blurb}
                    testId="marketing-asset-blurb"
                  />
                  <AssetCard
                    title="ARC Pitch Email"
                    icon={Mail}
                    iconColor="text-blue-500"
                    content={assets.arc_pitch_email}
                    testId="marketing-asset-arc"
                  />
                  <AssetCard
                    title="Tweet Thread (3 tweets)"
                    icon={Twitter}
                    iconColor="text-sky-500"
                    content={assets.tweet_thread}
                    testId="marketing-asset-tweets"
                  />
                  <AssetCard
                    title="Instagram Caption"
                    icon={Instagram}
                    iconColor="text-pink-500"
                    content={assets.instagram_caption}
                    testId="marketing-asset-instagram"
                  />
                  <AssetCard
                    title="Facebook Post"
                    icon={Facebook}
                    iconColor="text-indigo-500"
                    content={assets.facebook_post}
                    testId="marketing-asset-facebook"
                  />
                  <SocialSchedulerSection pid={pid} assets={assets} />
                </>
              )}
            </div>
          </TabsContent>

          {/* ── ARC Outreach tab ── */}
          <TabsContent value="arc" className="flex-1 overflow-y-auto mt-0 pt-3 pr-1">
            <ArcOutreachTab pid={pid} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
